/**
 * Centralized localStorage utility with proper error handling and fallbacks
 * Handles cases where localStorage is disabled, unavailable, or throws exceptions
 */

interface StorageUtils {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => boolean;
  removeItem: (key: string) => boolean;
  getJSON: <T>(key: string, defaultValue: T) => T;
  setJSON: <T>(key: string, value: T) => boolean;
  isAvailable: () => boolean;
}

class LocalStorageWrapper implements StorageUtils {
  private available: boolean | null = null;

  /**
   * Check if localStorage is available and functional
   */
  isAvailable(): boolean {
    if (this.available !== null) {
      return this.available;
    }

    try {
      // Check if localStorage exists
      if (typeof Storage === 'undefined' || !window.localStorage) {
        this.available = false;
        return false;
      }

      // Test if we can actually use it (some browsers allow access but throw on usage)
      const testKey = '__localStorage_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);

      this.available = true;
      return true;
    } catch (error) {
      // localStorage is disabled (private browsing, security policies, etc.)
      console.warn('localStorage is not available:', error);
      this.available = false;
      return false;
    }
  }

  /**
   * Get item from localStorage with error handling
   */
  getItem(key: string): string | null {
    if (!this.isAvailable()) {
      return null;
    }

    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      console.warn(`Failed to get localStorage item "${key}":`, error);
      return null;
    }
  }

  /**
   * Set item in localStorage with error handling
   * Returns true if successful, false if failed
   */
  setItem(key: string, value: string): boolean {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (error) {
      // Handle quota exceeded, security errors, etc.
      console.warn(`Failed to set localStorage item "${key}":`, error);
      return false;
    }
  }

  /**
   * Remove item from localStorage with error handling
   * Returns true if successful, false if failed
   */
  removeItem(key: string): boolean {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      window.localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Failed to remove localStorage item "${key}":`, error);
      return false;
    }
  }

  /**
   * Get JSON item from localStorage with parsing and error handling
   * Returns defaultValue if item doesn't exist, parsing fails, or localStorage is unavailable
   */
  getJSON<T>(key: string, defaultValue: T): T {
    const item = this.getItem(key);

    if (item === null) {
      return defaultValue;
    }

    try {
      return JSON.parse(item) as T;
    } catch (error) {
      console.warn(`Failed to parse JSON from localStorage item "${key}":`, error);
      return defaultValue;
    }
  }

  /**
   * Set JSON item in localStorage with stringification and error handling
   * Returns true if successful, false if failed
   */
  setJSON<T>(key: string, value: T): boolean {
    try {
      const serialized = JSON.stringify(value);
      return this.setItem(key, serialized);
    } catch (error) {
      console.warn(`Failed to serialize JSON for localStorage item "${key}":`, error);
      return false;
    }
  }
}

// Create singleton instance
const localStorageUtils = new LocalStorageWrapper();

export default localStorageUtils;

// Export individual functions for convenience
export const { getItem, setItem, removeItem, getJSON, setJSON, isAvailable } = localStorageUtils;

// Export specific storage keys as constants to avoid typos
export const STORAGE_KEYS = {
  MOST_RECENTLY_VIEWED_WORKSPACE: 'MOST_RECENTLY_VIEWED_WORKSPACE',
  RECENT_WORKSPACES: 'RECENT_WORKSPACES',
  UI_THEME: 'uiTheme',
  USER_TIMEZONE: 'userTimezone',
  WHATS_NEW_LAST_CHECKED: 'whats-new-last-checked',
  SHOW_NEW_FCO_LISTS: 'SHOW_NEW_FCO_LISTS',
  SHOW_DATA_OBSERVABILITY_METRICS: 'SHOW_DATA_OBSERVABILITY_METRICS',
  SHOW_SERVER_GROUPS: 'SHOW_SERVER_GROUPS',
  SHOW_NEW_UI: 'SHOW_NEW_UI',
  SHOW_FEATURES_V2: 'SHOW_FEATURES_V2',
} as const;
